import { Fragment, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { GitBranch, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { useJobRelated } from '@/hooks/jobs/useJobs';
// NOT-560 — pipeline-status chip for estimates listed on a service call.
import { EstimateStatusBadge } from '@/components/organisms/estimates/EstimateStatusBadge';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import { formatDateTimeInTimeZone } from '@/lib/date-values';

// Search params the estimate detail route requires; mirrors the constant used
// elsewhere for estimate-detail links (NOT-246).
const DEFAULT_ESTIMATE_DETAIL_SEARCH = {
  ...DEFAULT_ESTIMATE_LIST_FILTERS,
  tab: 'overview' as const,
};

function formatDate(iso?: string | null, timeZone?: string | null): string {
  if (!iso) return 'date unavailable';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'date unavailable';
  return formatDateTimeInTimeZone(iso, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(value?: number | null): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value ?? 0);
}

/**
 * NOT-560 — call ↔ estimates ↔ child-jobs navigation. Shared between the job
 * detail page and the Service-call (booking) detail view: the whole point of a
 * service call is call → estimate(s) → converted job, and that chain must be
 * visible from wherever the record is opened.
 */
/**
 * The related-record chain, grouped once for both renderings: the vertical card
 * (booking and appointment views) and the strip that keeps the chain above the
 * fold on the job overview. Returns null when there is nothing to link to.
 */
function useJobRelatedGroups(jobId: string) {
  const relatedQuery = useJobRelated(jobId);
  const related = relatedQuery.data;

  return useMemo(() => {
    if (!related) return null;

    const estimates = related.estimates ?? [];
    const childJobs = related.downstreamJobs ?? [];
    const sourceJob = related.sourceJob ?? null;

    if (estimates.length === 0 && childJobs.length === 0 && !sourceJob) return null;

    return {
      sourceJob,
      // NOT-565 — a callback (RECALL) points back at the job it redoes; the copy
      // on the back-link differs from the service-call copy.
      isRecall: related.job?.source === 'RECALL',
      // The estimate that BECAME this job, kept apart from the ones raised on it.
      // Both were listed under "Estimates on this call", where the document that
      // created the work read as just another follow-up.
      originEstimates: estimates.filter((e) => e.relationship === 'CONVERTED_INTO_JOB'),
      raisedEstimates: estimates.filter((e) => e.relationship !== 'CONVERTED_INTO_JOB'),
      callbackChildren: childJobs.filter((cj) => cj.source === 'RECALL'),
      otherChildren: childJobs.filter((cj) => cj.source !== 'RECALL'),
    };
  }, [related]);
}

export function JobRelationshipsCard({
  jobId,
  pricingHidden,
}: {
  jobId: string;
  pricingHidden: boolean;
}) {
  const groups = useJobRelatedGroups(jobId);

  if (!groups) return null;

  const {
    sourceJob,
    isRecall,
    originEstimates,
    raisedEstimates,
    callbackChildren,
    otherChildren,
  } = groups;

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <header className="flex items-center gap-2 border-b border-border/70 px-4 py-3">
        <GitBranch className="size-4 text-muted-foreground" />
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Related
        </span>
      </header>
      <div className="divide-y divide-border/60">
        {/* This job points back at its origin — a callback links to the job it
            redoes; a service-call spawn links to the originating call. */}
        {sourceJob && (
          <div className="px-4 py-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {isRecall ? 'Return trip for' : 'Source call'}
            </p>
            <Link
              to="/dashboard/jobs/$id"
              params={{ id: sourceJob.jobId }}
              search={{ tab: 'overview' as const }}
              className="mt-1 inline-flex items-center gap-1 text-[13px] font-medium text-info hover:underline"
            >
              {isRecall
                ? `${sourceJob.jobNumber} — ${sourceJob.name}${
                    sourceJob.completedAt ? `, completed ${formatDate(sourceJob.completedAt)}` : ''
                  } →`
                : `Created from service call ${sourceJob.jobNumber} →`}
            </Link>
          </div>
        )}

        {/* The estimate this job came from. First, because it is the reason the
            job exists — the ones below were raised after it. */}
        {originEstimates.length > 0 && (
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {originEstimates.length === 1
                ? 'Estimate that created this job'
                : 'Estimates that created this job'}
            </p>
            <ul className="space-y-2">
              {originEstimates.map((est) => (
                <li key={est.id} className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Link
                      to="/dashboard/estimates/$id"
                      params={{ id: est.id }}
                      search={DEFAULT_ESTIMATE_DETAIL_SEARCH}
                      className="truncate text-[13px] font-medium text-info hover:underline"
                    >
                      {est.estimateNumber}
                    </Link>
                    <EstimateStatusBadge status={est.status} />
                  </div>
                  {!pricingHidden && (
                    <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground">
                      {formatMoney(est.grandTotal)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Estimates raised on this job (job → estimates), each with a forward
            link to the job it converted into when one exists. */}
        {raisedEstimates.length > 0 && (
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Estimates raised on this job
            </p>
            <ul className="space-y-2">
              {raisedEstimates.map((est) => (
                <li key={est.id} className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Link
                      to="/dashboard/estimates/$id"
                      params={{ id: est.id }}
                      search={DEFAULT_ESTIMATE_DETAIL_SEARCH}
                      className="truncate text-[13px] font-medium text-info hover:underline"
                    >
                      {est.estimateNumber}
                    </Link>
                    <EstimateStatusBadge status={est.status} />
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {!pricingHidden && (
                      <span className="text-[13px] tabular-nums text-muted-foreground">
                        {formatMoney(est.grandTotal)}
                      </span>
                    )}
                    {est.jobId && (
                      <Link
                        to="/dashboard/jobs/$id"
                        params={{ id: est.jobId }}
                        search={{ tab: 'overview' as const }}
                        className="text-xs font-medium text-info hover:underline"
                      >
                        Job →
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Callbacks that redo THIS job (original → recall children). */}
        {callbackChildren.length > 0 && (
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {callbackChildren.length > 1 ? 'Callbacks' : 'Callback'}
            </p>
            <ul className="space-y-2">
              {callbackChildren.map((cj) => (
                <li key={cj.jobId} className="flex items-center justify-between gap-3">
                  <Link
                    to="/dashboard/jobs/$id"
                    params={{ id: cj.jobId }}
                    search={{ tab: 'overview' as const }}
                    className="inline-flex min-w-0 items-center gap-1.5 truncate text-[13px] font-medium text-info hover:underline"
                  >
                    <RotateCcw className="size-3.5 shrink-0" />
                    {cj.jobNumber}
                    <span className="truncate text-muted-foreground">
                      · {formatDate(cj.createdAt)}
                    </span>
                  </Link>
                  <Badge variant="secondary">{cj.status.replaceAll('_', ' ')}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Jobs produced downstream from this call (call → child jobs). */}
        {otherChildren.length > 0 && (
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Child jobs
            </p>
            <ul className="space-y-2">
              {otherChildren.map((cj) => (
                <li key={cj.jobId} className="flex items-center justify-between gap-3">
                  <Link
                    to="/dashboard/jobs/$id"
                    params={{ id: cj.jobId }}
                    search={{ tab: 'overview' as const }}
                    className="truncate text-[13px] font-medium text-info hover:underline"
                  >
                    {cj.jobNumber}
                  </Link>
                  <Badge variant="secondary">{cj.status.replaceAll('_', ' ')}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

/** A chip in the strip: a labelled, clickable pointer at one related record. */
const CHAIN_CHIP =
  'inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12.5px] leading-5 shadow-sm transition-colors hover:border-foreground/25 hover:bg-accent';

/** The tiny uppercase word that says what kind of record the chip points at. */
const CHAIN_KIND = 'shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground';

/**
 * The same chain as {@link JobRelationshipsCard}, flattened into one wrapping
 * row of chips. The card lives at the foot of the job overview's left column,
 * below the visit list — so on a job with a couple of visits the estimate that
 * the whole job was sold on sits a screen and a half down the page, under the
 * least urgent thing on it. The chain is navigation, not detail: it belongs
 * above the fold, at roughly the height of a single row.
 */
export function JobRelatedStrip({
  jobId,
  pricingHidden,
}: {
  jobId: string;
  pricingHidden: boolean;
}) {
  const groups = useJobRelatedGroups(jobId);

  if (!groups) return null;

  const { sourceJob, isRecall, originEstimates, raisedEstimates, callbackChildren, otherChildren } =
    groups;

  // downstreamJobs only holds jobs whose sourceJobId points back at this one
  // (jobs.service findBySourceJobId). A job created by converting an estimate
  // raised here is not guaranteed to carry that back-pointer, so without this
  // the only route to it disappears off the overview.
  const childJobIds = new Set(
    [...callbackChildren, ...otherChildren].map((child) => child.jobId),
  );

  const money = (value?: number | null) =>
    pricingHidden ? null : (
      <span className="shrink-0 tabular-nums text-muted-foreground">{formatMoney(value)}</span>
    );

  return (
    <section
      aria-label="Related records"
      data-testid="job-related-strip"
      className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-xl border border-border bg-muted/30 px-3 py-2"
    >
      <span className="inline-flex shrink-0 items-center gap-1.5 pr-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <GitBranch className="size-3.5" />
        Related
      </span>

      {sourceJob && (
        <Link
          to="/dashboard/jobs/$id"
          params={{ id: sourceJob.jobId }}
          search={{ tab: 'overview' as const }}
          className={CHAIN_CHIP}
        >
          <span className={CHAIN_KIND}>{isRecall ? 'Redoes' : 'From call'}</span>
          <span className="truncate font-medium">{sourceJob.jobNumber}</span>
        </Link>
      )}

      {originEstimates.map((est) => (
        <Link
          key={est.id}
          to="/dashboard/estimates/$id"
          params={{ id: est.id }}
          search={DEFAULT_ESTIMATE_DETAIL_SEARCH}
          className={CHAIN_CHIP}
        >
          <span className={CHAIN_KIND}>Sold on</span>
          <span className="truncate font-medium">{est.estimateNumber}</span>
          <EstimateStatusBadge status={est.status} />
          {money(est.grandTotal)}
        </Link>
      ))}

      {raisedEstimates.map((est) => (
        <Fragment key={est.id}>
          <Link
            to="/dashboard/estimates/$id"
            params={{ id: est.id }}
            search={DEFAULT_ESTIMATE_DETAIL_SEARCH}
            className={CHAIN_CHIP}
          >
            <span className={CHAIN_KIND}>Estimate</span>
            <span className="truncate font-medium">{est.estimateNumber}</span>
            <EstimateStatusBadge status={est.status} />
            {money(est.grandTotal)}
          </Link>
          {/* A sibling chip, never nested: an <a> inside an <a> is invalid and
              breaks hydration. Skipped when the job already has its own chip
              below, so the strip does not say the same thing twice. */}
          {est.jobId && !childJobIds.has(est.jobId) ? (
            <Link
              to="/dashboard/jobs/$id"
              params={{ id: est.jobId }}
              search={{ tab: 'overview' as const }}
              className={CHAIN_CHIP}
            >
              <span className={CHAIN_KIND}>Converted to</span>
              <span className="truncate font-medium">Job →</span>
            </Link>
          ) : null}
        </Fragment>
      ))}

      {callbackChildren.map((cj) => (
        <Link
          key={cj.jobId}
          to="/dashboard/jobs/$id"
          params={{ id: cj.jobId }}
          search={{ tab: 'overview' as const }}
          className={CHAIN_CHIP}
        >
          <RotateCcw className="size-3 shrink-0 text-muted-foreground" />
          <span className={CHAIN_KIND}>Callback</span>
          <span className="truncate font-medium">{cj.jobNumber}</span>
          <Badge variant="secondary">{cj.status.replaceAll('_', ' ')}</Badge>
        </Link>
      ))}

      {otherChildren.map((cj) => (
        <Link
          key={cj.jobId}
          to="/dashboard/jobs/$id"
          params={{ id: cj.jobId }}
          search={{ tab: 'overview' as const }}
          className={CHAIN_CHIP}
        >
          <span className={CHAIN_KIND}>Child job</span>
          <span className="truncate font-medium">{cj.jobNumber}</span>
          <Badge variant="secondary">{cj.status.replaceAll('_', ' ')}</Badge>
        </Link>
      ))}
    </section>
  );
}
